import { FlexiAdvisorAssignment, FlexiBusinessAdvisor } from './flexi-advisor-settings.models';

/**
 * Pure helpers behind Flexi › Settings. Kept out of the components so the
 * rules the dialogs preview can be tested on their own.
 */

/**
 * The advisor a business would see for this ordered list: the first eligible
 * one. Mirrors FlexiHseAdvisorResolver on the backend, which decides for real
 * on save — this only drives the live preview.
 */
export function primaryOf(advisors: FlexiBusinessAdvisor[]): FlexiBusinessAdvisor | null {
  return advisors.find(advisor => advisor.IsEligible) ?? null;
}

/** The list re-numbered after a reorder, add or remove, with the primary re-marked. */
export function renumber(advisors: FlexiBusinessAdvisor[]): FlexiBusinessAdvisor[] {
  const primary = primaryOf(advisors);
  return advisors.map((advisor, index) => ({
    ...advisor,
    SortOrder: index,
    IsPrimary: advisor === primary,
  }));
}

/** Moves one advisor up (-1) or down (+1); a move past either end is ignored. */
export function move(advisors: FlexiBusinessAdvisor[], index: number, offset: -1 | 1): FlexiBusinessAdvisor[] {
  const target = index + offset;
  if (target < 0 || target >= advisors.length) {
    return advisors;
  }
  const next = [...advisors];
  [next[index], next[target]] = [next[target], next[index]];
  return renumber(next);
}

/** One business losing advisors to a bulk replace. */
export interface ReplaceRemoval {
  businessName: string;
  advisorNames: string[];
}

/**
 * What a bulk REPLACE would take away, per selected business, so the dialog
 * can warn before existing advisors are overwritten.
 */
export function replaceRemovals(
  businesses: FlexiAdvisorAssignment[],
  userIds: number[],
): ReplaceRemoval[] {
  return businesses
    .map(business => ({
      businessName: business.CompanyName ?? '',
      advisorNames: business.Advisors
        .filter(advisor => userIds.indexOf(advisor.UserId) === -1)
        .map(advisor => advisor.Name ?? ''),
    }))
    .filter(removal => removal.advisorNames.length > 0);
}

/** Up to two initials for an avatar: "Kari Nordmann" → "KN". */
export function initials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(part => part.length > 0);
  if (parts.length === 0) {
    return '?';
  }
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
}
